import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Gamepad2, Trophy, Play, Users, Hand } from 'lucide-react';
import { NetworkManager } from './NetworkManager';
import { listGames, getGame } from './framework/GamesRegistry';
import type { GameId, GameManifest, GameRuntimeProps } from './framework/types';
// SnakeActivity (the multiplayer voice-channel Snake) self-registers via
// `registerGame({ id: 'snake', ... })` at module load. Imported here so
// STATIC_COMPONENTS can dispatch by id and so the registry is populated
// synchronously at startup.
import SnakeActivity from '../components/games/SnakeActivity';
// CyberChessActivity is the other voice-channel register-on-import game.
// Its module-level `registerGame({ id: 'chess', multiplayer: true, ... })`
// call fires on import; the server's `role-assignment` socket event hands
// out white/black/spectator slots so the original "both sides spawn as
// white" bug cannot recur.
import CyberChessActivity from '../components/games/CyberChessActivity';
// Single-player iframe launchers — each calls `createIframeGame(...)` at
// module load, populating GamesRegistry with id 'memory-matrix' / 'gooch'
// / 'kill-trump' / 'snake-legacy' / 'koumori-sensei' / 'freedoom'. The
// legacy entry list (UwU Invaders, CyberChessGame, MemoryMatrixGame, the
// bare IframeGame iframe stubs) has been retired; the surviving
// single-player games moved into these launchers. FreeDoom references the
// vendored assets at /public/freedoom/ — see public/freedoom/THIRD_PARTY_ASSETS.md
// §11 for v1 (non-competitive, leaderboard=null) scope.
import MemoryMatrixLauncher from '../components/games/launchers/MemoryMatrixLauncher';
import GoochInvadersLauncher from '../components/games/launchers/GoochInvadersLauncher';
import KillTrumpLauncher from '../components/games/launchers/KillTrumpLauncher';
import LegacySnakeLauncher from '../components/games/launchers/LegacySnakeLauncher';
import KoumoriSenseiLauncher from '../components/games/launchers/KoumoriSenseiLauncher';
// FreeDoom single-player voice activity. Non-competitive in v1 per the
// audit; relies on the vendored js-dos@8.4.1 + FreeDoom IWAD assets under
// `/public/freedoom/` (deployed at build time per
// `public/freedoom/THIRD_PARTY_ASSETS.md`).
import FreeDoomLauncher from '../components/games/launchers/FreeDoomLauncher';
// Quake 3 Arena (QuakeJS) — multiplayer voice-channel FPS. Same-origin
// iframe launcher (`/quake3/index.html`) that boots the vendored
// ioquake3.js client; networking is QuakeJS's own WebSocket to a
// dedicated ioq3ded.js server, NOT the framework's socket.io (which only
// coordinates the arcade host/request flow).
import Quake3Launcher from '../components/games/launchers/Quake3Launcher';
// TOSIOS (MIT) — 2D top-down arena shooter. Cross-origin iframe to a
// TOSIOS server (Express + Colyseus on :3001); networking is Colyseus
// WebSocket inside the iframe. Co-location via the shared room list (or
// the optional auto-join deep-link patch).
import TosiosLauncher from '../components/games/launchers/TosiosLauncher';
import '../components/games/launchers/KoumoriSenseiManifest';
import DrawTogetherActivity from '../components/games/DrawTogetherActivity';
import '../components/games/launchers/DrawTogetherLauncher';
import CyberChessSoloLauncher from '../components/games/launchers/CyberChessSoloLauncher';
import DrawTogetherSoloLauncher from '../components/games/launchers/DrawTogetherSoloLauncher';

// Render-time boundary that catches crashes from WASM activities (Snake
// WASM, CyberChess WASM, etc. as they adopt the runtime). One broken
// activity must not lock up the entire CallLayout activity surface. The
// boundary itself paints a diagnostic overlay; we don't try to re-mount.
import ErrorBoundary from '../ErrorBoundary';

interface RegistryEntry {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  component: React.ComponentType<any>;
  soloComponent?: React.ComponentType<any>;
  multiplayer: boolean;
  /** True when the game can be played alone (either solo-only, or dual-mode). */
  solo: boolean;
}

/**
 * Static dispatch table: maps a registered game id to its concrete component
 * so buildGameEntries() doesn't need an `if (id === '...') return Component`
 * chain every time a new game migrates into the registry. New register-on-
 * import games add an entry here; legacy entries stay in LEGACY_GAMES until
 * they're migrated.
 */
const STATIC_COMPONENTS: Record<string, React.ComponentType<any>> = {
  snake: SnakeActivity,
  chess: CyberChessActivity,
  'memory-matrix': MemoryMatrixLauncher,
  gooch: GoochInvadersLauncher,
  'kill-trump': KillTrumpLauncher,
  // FreeDoom single-player DOSBox-WASM iframe (/freedoom/index.html).
  // Non-competitive in v1 (leaderboard=null). The bridge emits
  // psydechat:* envelopes the Activity currently drops by design;
  // FreeDoom is non-competitive in v1; see below for the time_ms
  // scoring follow-up.
  freedoom: FreeDoomLauncher,
  quake3: Quake3Launcher,
  tosios: TosiosLauncher,
  'snake-legacy': LegacySnakeLauncher,
  'koumori-sensei': KoumoriSenseiLauncher,
  'draw-together': DrawTogetherActivity,
  // Future activity migrations add their module import and a
  // STATIC_COMPONENTS entry above. No other line in this file needs to
  // change.
};

/**
 * Solo-mode overrides. A game listed here declares `solo: true` in its
 * manifest and renders THIS component when the user picks Solo on its
 * tile, instead of the multiplayer component in STATIC_COMPONENTS above.
 *
 * These two used to be separate registry entries ('chess-solo',
 * 'draw-together-solo'), so CyberChess and DrawTogether each appeared
 * twice in the arcade picker under different names. One manifest now
 * declares both capabilities and the mode is chosen at launch time.
 *
 * A game whose manifest has `multiplayer: false` needs no entry here —
 * its single STATIC_COMPONENTS component already is the solo experience.
 */
const SOLO_COMPONENTS: Record<string, React.ComponentType<any>> = {
  // Wraps CyberChessActivity with mode='single-player' so NetworkManager
  // is bypassed and the AI move loop runs locally via aiService.
  chess: CyberChessSoloLauncher,
  // Wraps DrawTogetherActivity with roomId={null} so no network manager
  // fires; all drawing is local state only.
  'draw-together': DrawTogetherSoloLauncher,
};

/**
 * Resolve the framework registry into the GameManifest-shaped entries
 * GameManager renders. STATIC_COMPONENTS provides the per-game React
 * component (SnakeActivity, CyberChessActivity, plus the four single-
 * player iframe launchers). Manifests without a wired component fall
 * back to a no-op placeholder so the framework can still enumerate
 * them without exploding.
 */
function buildGameEntries(): RegistryEntry[] {;
  return listGames()
    .filter((g) => g.available)
    .map((g) => ({
      id: g.id,
      name: g.displayName,
      description: g.shortDescription ?? '',
      icon: g.icon ? React.createElement(g.icon, { size: 36 }) : <Gamepad2 size={36} />,
      component: STATIC_COMPONENTS[g.id] ?? (() => null),
      soloComponent: SOLO_COMPONENTS[g.id],
      multiplayer: !!g.multiplayer,
      // A non-multiplayer game is solo by definition; a multiplayer game is
      // only also-solo when its manifest says so.
      solo: !g.multiplayer || !!g.solo,
    }));
}

export default function GameManager({
  initialRoomId,
  initialGameId,
  playerSelf,
  onExit,
  onChange,
  onLaunchLeaderboard,
}: {
  initialRoomId?: string;
  initialGameId?: GameId | null;
  /**
   * The local user's identity, forwarded straight through to whichever
   * GameComponent mounts. Single-player activities (CyberChess solo, via
   * CyberChessSoloLauncher) need this to seed their local game state —
   * without it `playerSelf` is undefined and those activities never
   * initialize. Multiplayer activities derive their own identity from
   * NetworkManager's role-assignment instead and can ignore this.
   */
  playerSelf?: GameRuntimeProps['playerSelf'];
  onExit?: () => void;
  /**
   * Forwarded to the GameComponent so the runtime can ping lifecycle
   * changes back to the host. FreeDoomActivity, for example, calls
   * `onChange('freedoom')` on mount and `onChange(null)` on unmount or
   * explicit Exit. CallLayout wires this to its local state + final
   * upward bubble to App.tsx's `setActiveGame` so the voice-channel 🎮
   * pill lights up on every enter path, not just rail launches.
   */
  onChange?: (gameId: GameId | null) => void;
  /**
   * Opens the NEURAL_CHAMPIONS leaderboard panel. This lobby is now the
   * only game-picking surface (see the note on CallLayout's Games button —
   * there used to be a second, differently-styled picker (GameMenu.tsx)
   * that also carried its own leaderboard footer CTA), so this lobby needs
   * its own entry point to that panel.
   */
  onLaunchLeaderboard?: () => void;
}) {
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  // Which variant of `selectedGame` to mount. Only meaningful for games
  // that declare both modes; single-mode games ignore it.
  const [selectedMode, setSelectedMode] = useState<'multiplayer' | 'solo'>('multiplayer');
  const networkRef = useRef<NetworkManager | null>(null);
  const [isHost, setIsHost] = useState(true);
  const [hostGameId, setHostGameId] = useState<string | null>(null);
  const requestedGameRef = useRef<string | null>(null);
  // Transient banner shown to the host when a peer asks to play something,
  // and to a peer confirming their request was sent.
  const [requestNotice, setRequestNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!initialRoomId) return;
    const nm = new NetworkManager(initialRoomId, 'lobby');
    networkRef.current = nm;
    nm.onHostAssignment = setIsHost;
    nm.onGameChanged = (gameId) => {
      // A host-driven game change is always the multiplayer variant —
      // solo launches never round-trip through the server.
      setHostGameId(gameId || null);
      if (!gameId) {
        setSelectedGame(null);
      } else if (gameId === requestedGameRef.current) {
        setSelectedMode('multiplayer');
        setSelectedGame(gameId);
        requestedGameRef.current = null;
      }
    };
    nm.onGameRequested = ({ gameType }) => {
      const label = getGame(gameType as GameId)?.displayName ?? gameType;
      setRequestNotice(`A player wants to play ${label} — start it to let them in.`);
    };
    return () => {
      nm.disconnect();
      networkRef.current = null;
    };
  }, [initialRoomId]);

  // Auto-dismiss the request banner so it doesn't linger forever.
  useEffect(() => {
    if (!requestNotice) return;
    const t = setTimeout(() => setRequestNotice(null), 6000);
    return () => clearTimeout(t);
  }, [requestNotice]);

  // When the parent (CallLayout via the GamesRail) passes an explicit
  // `initialGameId`, auto-select that manifest in the registry so the
  // activity opens the right game rather than the lobby landing page.
  // Tolerates late-arriving props (re-mounts, remounts with a different
  // pick) by reacting to changes via deps. The network-manager call is a
  // no-op when the host hasn't connected yet — local state alone is
  // enough for solo/peek paths; the multiplayer dispatcher picks the
  // change up on the next `changeGame` round-trip.
  useEffect(() => {
    if (!initialGameId) return;
    const manifest = getGame(initialGameId);
    if (!manifest) return;
    setSelectedGame(initialGameId);
    if (isHost) {
      networkRef.current?.changeGame(initialGameId);
    }
  }, [initialGameId, isHost]);

  /** Start a game locally in solo mode — never touches the network. */
  const handlePlaySolo = (gameId: string) => {
    setSelectedMode('solo');
    setSelectedGame(gameId);
  };

  /** Host-only: start a multiplayer session and broadcast it to the room. */
  const handleHostGame = (gameId: string) => {
    setSelectedMode('multiplayer');
    setSelectedGame(gameId);
    networkRef.current?.changeGame(gameId);
  };

  /**
   * Non-host: ask the host to start this game. The server relays it to the
   * host socket, which surfaces `requestNotice` on their screen. When the
   * host obliges, `onGameChanged` pulls everyone (including this user) into
   * the activity automatically.
   */
  const handleRequestGame = (gameId: string, label: string) => {
    requestedGameRef.current = gameId;
    networkRef.current?.requestGame(gameId);
    setRequestNotice(`Asked the host to start ${label}. You'll join automatically.`);
  };

  const handleExitGame = () => {
    setSelectedGame(null);
    // Only clear the shared room's game when we actually started it there.
    // A solo session was never broadcast, so clearing would yank every
    // other participant out of a multiplayer game they're still playing.
    if (isHost && selectedMode === 'multiplayer') {
      networkRef.current?.changeGame('');
    }
    setSelectedMode('multiplayer');
  };

  const allEntries = useMemo(() => buildGameEntries(), []);

  if (selectedGame) {
    const game = allEntries.find(g => g.id === selectedGame);
    if (game) {
      // Dual-mode games (chess, draw-together) render a different component
      // per mode; everything else has exactly one.
      const GameComponent =
        selectedMode === 'solo' && game.soloComponent
          ? game.soloComponent
          : game.component;
      return (
        // The ErrorBoundary isolates any render-time crash to its own
        // diagnostic overlay rather than blanking the entire activity
        // surface. The boundary's fallback UI is positioned fixed/inset:0
        // and z-index 10000 so it always paints above the call layout.
        <ErrorBoundary>
          <GameComponent
            initialRoomId={initialRoomId}
            // DrawTogetherActivity (the multiplayer 'draw-together' entry)
            // has its own bespoke prop interface predating GameRuntimeProps
            // — it reads `roomId`, not `initialRoomId`. Without this it
            // silently fell back to its hardcoded 'vc' default, so every
            // voice channel's multiplayer DrawTogether session collided
            // into the same shared room regardless of which channel
            // launched it. Harmless extra prop for every other game.
            roomId={initialRoomId}
            playerSelf={playerSelf}
            onExit={handleExitGame}
            onChange={onChange}
          />
        </ErrorBoundary>
      );
    }
  }

  return (
    <div className="flex flex-col items-center justify-center w-full h-full bg-[#0a0612]/95 p-8 text-white font-mono rounded-3xl overflow-y-auto">
      <h1 className="text-4xl font-black mb-2 italic tracking-tighter text-cyan-400 shadow-text">NEURAL_ARCADE.SYS</h1>
      <p className="text-xs text-white/40 uppercase tracking-[0.2em] mb-6">Select Voice Activity Simulation</p>

      {requestNotice && (
        <div className="mb-6 px-5 py-2.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-200 text-[11px] font-bold uppercase tracking-widest text-center">
          {requestNotice}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-4xl">
        {allEntries.map(game => {
          // One tile per game. A game that supports both modes shows both
          // actions here rather than existing twice in the registry (which
          // is how chess/chess-solo and draw-together/draw-together-solo
          // used to appear — the same game listed twice under two names).
          const canHostMp = game.multiplayer && isHost;
          const isHostPlayingThis = hostGameId === game.id;
          const canRequestMp = game.multiplayer && !isHost && !hostGameId;
          const canJoinMp = game.multiplayer && !isHost && isHostPlayingThis;

          return (
            <div
              key={game.id}
              className="flex flex-col items-center gap-3 p-6 rounded-3xl border border-white/10 bg-black/40 backdrop-blur-md transition-all group hover:border-cyan-400/50"
            >
              <div className="p-4 rounded-2xl bg-white/5 group-hover:scale-110 transition-transform text-cyan-400">
                {React.cloneElement(game.icon as React.ReactElement<any>, { size: 36 })}
              </div>
              <span className="font-bold text-md uppercase tracking-wider text-center">{game.name}</span>
              {game.description && (
                <p className="text-[10px] text-white/40 text-center leading-relaxed line-clamp-2 font-sans">
                  {game.description}
                </p>
              )}

              <div className="flex flex-col gap-2 w-full mt-auto pt-2">
                {game.solo && (
                  <button
                    onClick={() => handlePlaySolo(game.id)}
                    className="w-full px-4 py-2 rounded-xl bg-cyan-500/15 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/25 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Play size={12} /> {game.multiplayer ? 'Play Solo' : 'Play'}
                  </button>
                )}

                {canHostMp && (
                  <button
                    onClick={() => handleHostGame(game.id)}
                    className="w-full px-4 py-2 rounded-xl bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Users size={12} /> Host Multiplayer
                  </button>
                )}

                {/* The old UI dead-ended here with an inert "WAITING FOR
                    HOST..." label. Now a peer can actually ask for it. */}
                {canRequestMp && (
                  <button
                    onClick={() => handleRequestGame(game.id, game.name)}
                    className="w-full px-4 py-2 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/25 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Hand size={12} /> Request to Play
                  </button>
                )}
                {canJoinMp && (
                  <button
                    onClick={() => {
                      setSelectedMode('multiplayer');
                      setSelectedGame(game.id);
                    }}
                    className="w-full px-4 py-2 rounded-xl bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 transition-colors text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Play size={12} /> Join Host's Game
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-12">
        {onLaunchLeaderboard && (
          <button onClick={onLaunchLeaderboard} className="flex items-center gap-2 px-8 py-3 rounded-full border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 transition-colors uppercase font-bold tracking-widest text-xs">
            <Trophy size={14} /> Leaderboards
          </button>
        )}
        {onExit && (
          <button onClick={onExit} className="px-8 py-3 rounded-full border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors uppercase font-bold tracking-widest text-xs">
            Disconnect Arcade
          </button>
        )}
      </div>
    </div>
  );
}
